import { injectScript } from './script';
import { t } from '@lingui/macro';

export interface CaptchaOptions {
  enabled?: boolean | null;
  badge?: string;
  callback?: ((responseToken: string | null) => void) | null;
  errorCallback?: (error?: string) => void;
  expiredCallback?: () => void;
  language?: string;
  size?: 'compact' | 'invisible' | 'normal';
  theme?: 'dark' | 'light';
  globally?: boolean;
}

export const getRecaptchaHost = (globally?: boolean): string => {
  return globally
    ? 'https://www.recaptcha.net/recaptcha/api.js'
    : 'https://www.google.com/recaptcha/api.js';
};

export const initBaseCss = (captchaRef?: HTMLDivElement | null): void => {
  if (!captchaRef) return;

  captchaRef.style.height = '0px';
  captchaRef.style.overflow = 'hidden';
  captchaRef.style.transition = 'height .278s';
};

export const hideCaptcha = (captchaRef?: HTMLDivElement | null): void => {
  if (captchaRef) {
    captchaRef.style.height = '0px';
  }
};

export const showCaptcha = (
  captchaRef?: HTMLDivElement | null,
  size?: 'compact' | 'normal' | 'invisible'
): void => {
  const isCompactSize = size === 'compact' || size === 'invisible';

  if (captchaRef) {
    captchaRef.style.height = isCompactSize ? '140px' : '80px';
  }
};

export const getCaptchaOptions = (
  sitekey: string,
  options: CaptchaOptions
): Record<string, unknown> => ({
  badge: options.badge || '',
  callback: options.callback || null,
  'error-callback': options.errorCallback || null,
  'expired-callback': options.expiredCallback || null,
  hl: options.language || '',
  sitekey: sitekey,
  size: options.size || '',
  theme: options.theme || '',
});

export class Captcha {
  public response: string | null = null;
  private executeResolver: ((response: string | null) => void) | null = null;
  private executeReject: ((response: string) => void) | null = null;
  private captchaId = 0;
  constructor(
    protected sitekey: string,
    protected options: CaptchaOptions = {},
    protected captchaRef: HTMLDivElement
  ) {
    this.initBaseCss();
    this.init();
  }

  public initBaseCss = (): void => {
    if (!this.captchaRef) return;

    this.captchaRef.style.height = '0px';
    this.captchaRef.style.overflow = 'hidden';
    this.captchaRef.style.transition = 'height .278s';
  };

  private setResponse = (response: string | null): void => {
    this.response = response;
    if (this.response && this.executeResolver) {
      this.executeResolver(response);
    }
  };

  private expiredCallback = (): void => {
    console.error('CAPTCHA TIMEOUT');

    this.options.expiredCallback && this.options.expiredCallback();
    this.executeReject && this.executeReject(t`reCaptcha expired: please retry submitting`);
  };

  private errorCallback = (error?: string): void => {
    console.error('CAPTCHA ERROR: ', error);

    this.options.errorCallback && this.options.errorCallback(error);
    this.executeReject && this.executeReject(t`reCaptcha error: please refresh and try again`);
  };

  private callback = (capResponse: string | null): void => {
    this.options.size !== 'invisible' && hideCaptcha(this.captchaRef);
    this.setResponse(capResponse);

    if (typeof this.options.callback === 'function') {
      this.options.callback(capResponse);
    }

    window.grecaptcha.reset(this.captchaId);
  };

  private initialized = false;
  private initializeFailed = false;
  public init = (): void => {
    if (!this.options.enabled) return;

    // eslint-disable-next-line prefer-const
    let timerId: ReturnType<typeof setInterval>;

    const src = getRecaptchaHost(this.options.globally);
    if (typeof window?.grecaptcha === 'undefined') injectScript(src);

    const renderCaptcha = (): void => {
      try {
        if (
          typeof window.grecaptcha !== 'undefined' &&
          (window.grecaptcha.render ?? window.grecaptcha.enterprise?.render) &&
          this.captchaRef
        ) {
          const grecaptcha = window.grecaptcha.enterprise ?? window.grecaptcha;
          const captchaOptions = getCaptchaOptions(this.sitekey, {
            ...this.options,
            callback: this.callback,
            errorCallback: this.errorCallback,
            expiredCallback: this.expiredCallback,
          });
          this.captchaId = grecaptcha.render(this.captchaRef, captchaOptions);
          this.initialized = true;
          clearInterval(timerId);
        } else {
          console.warn(`Can't initialize reCaptcha. Retrying.`);
        }
      } catch (e) {
        this.initializeFailed = true;
        console.error('reCaptcha init error.', e);
        throw e;
      }
    };

    timerId = setInterval(renderCaptcha, 824);
  };

  public ready = async (): Promise<boolean> => {
    return await new Promise<boolean>((resolve) => {
      const timerId = setInterval(() => {
        if (!this.initialized && !this.initializeFailed) return;
        clearInterval(timerId);
        resolve(this.initialized);
      }, 824);
    });
  };

  public execute = async (): Promise<string | null> => {
    return await new Promise<string | null>((resolve, reject) => {
      this.setResponse(null);

      this.executeResolver = resolve;
      this.executeReject = reject;

      const size = this.options.size;
      const isInvisibleSize = size === 'invisible';
      const grecaptcha = window.grecaptcha?.enterprise ?? window.grecaptcha;

      isInvisibleSize ? grecaptcha.execute(this.captchaId) : showCaptcha(this.captchaRef, size);
    });
  };
}
